<script setup>
/**
 * The ticket queue — the screen ticket handlers live in: scan, filter, open.
 *
 * The route query is the single source of truth for what the list shows. A
 * filter change writes the query; a query change (including the back button
 * and a pasted link) applies it to the store and refetches. Nothing filters,
 * sorts or paginates on the client: `queryParams` in the store is the one
 * translation into the REST contract, and the server answers with the page.
 *
 * `router.replace` rather than `push` for filter changes — a filter is not a
 * place, and narrowing a queue five times should not cost five back presses.
 * Opening a case is a real navigation and pushes.
 *
 * The queue has two projections of the same query: the list, and a kanban
 * board. The projection is a per-device preference (localStorage), not queue
 * state, so it never rides the URL.
 *
 * Both projections show the same rows, which is what decides how the board is
 * grouped. Stages belong to one ticket type, so a board of stage columns can
 * only show that type; used while the queue spans several, it drops every other
 * type's tickets on the floor. So stage columns are only drawn once the type
 * filter has narrowed the queue to a single type. Unnarrowed, the board groups
 * by the system stage category instead — the vocabulary every type's stages map
 * onto — and its four columns cover exactly the rows the list shows.
 */
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useStorage } from '@vueuse/core';

import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import CaseBoard from 'dashboard/components-next/SupportCases/CaseBoard.vue';
import CaseCreateDialog from 'dashboard/components-next/SupportCases/CaseCreateDialog.vue';
import CaseFilterBar from 'dashboard/components-next/SupportCases/CaseFilterBar.vue';
import CaseListTable from 'dashboard/components-next/SupportCases/CaseListTable.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import {
  BOARD_GROUP_BY,
  useSupportCasesStore,
} from 'dashboard/stores/supportCases';
import {
  STAGE_CATEGORY,
  STAGE_CATEGORY_META,
} from 'dashboard/helper/supportCaseMeta';
import {
  filtersFromQuery,
  hasActiveFilters,
  queryFromFilters,
} from './caseListQuery';

// Kaminari's page size for this API, fixed in docs/ticket-engine/api.md.
const PER_PAGE = 25;

const VIEW_MODE = { LIST: 'list', BOARD: 'board' };

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const store = useSupportCasesStore();
const { cases, counts, filters, meta, uiFlags, caseTypeOptions } =
  storeToRefs(store);

const hasError = ref(false);
const createDialogRef = ref(null);

const viewMode = useStorage('support_cases_view_mode', VIEW_MODE.LIST);
const isBoard = computed(() => viewMode.value === VIEW_MODE.BOARD);

const isFiltered = computed(() => hasActiveFilters(filters.value));

// The type whose workflow the board renders, or null while the queue spans more
// than one. Only `GET /case_types` carries `workflow_stages`, so a type filter
// set from a pasted URL resolves to null until that request lands — during which
// the board is grouped by category, which is right either way.
const boardType = computed(
  () =>
    caseTypeOptions.value.find(type => type.id === filters.value.caseTypeId) ||
    null
);

const isCategoryBoard = computed(() => !boardType.value);

const boardGroupBy = computed(() =>
  isCategoryBoard.value ? BOARD_GROUP_BY.CATEGORY : BOARD_GROUP_BY.STAGE
);

// A category column stands in for a stage it has no single answer for, so it
// carries a stage-shaped façade: enough for the header to render, and no
// `id`, which is how the drop handler tells the two kinds of column apart.
const categoryColumn = category => ({
  key: category,
  stage: {
    id: null,
    category,
    name: t(STAGE_CATEGORY_META[category].labelKey),
    wipLimit: null,
  },
  ...store.boardColumnFor(category),
});

const boardColumns = computed(() => {
  if (isCategoryBoard.value) {
    return Object.values(STAGE_CATEGORY).map(categoryColumn);
  }
  return (boardType.value.workflowStages || []).map(stage => ({
    key: stage.id,
    stage,
    ...store.boardColumnFor(stage.id),
  }));
});

// The header's total. The list fetch's meta does not move in board mode, so
// the board answers with what its columns add up to — the same scope, summed
// per stage by the server.
const totalCount = computed(() =>
  isBoard.value
    ? boardColumns.value.reduce((sum, column) => sum + column.meta.count, 0)
    : meta.value.count
);

const loadBoard = () => {
  const keys = boardColumns.value.map(column => column.key);
  if (!keys.length) return Promise.resolve();
  return store.fetchBoard(keys, { groupBy: boardGroupBy.value });
};

const load = async () => {
  hasError.value = false;
  try {
    // Counts report the facets the list is not narrowed by, so they ride the
    // same query and are refreshed with it rather than fetched once on mount.
    await Promise.all([
      isBoard.value ? loadBoard() : store.fetchCases(),
      store.fetchCounts(),
    ]);
  } catch (error) {
    hasError.value = true;
  }
};

const applyQuery = query => {
  store.setFilters(filtersFromQuery(query));
  load();
};

const writeQuery = () => {
  router.replace({ query: queryFromFilters(filters.value) });
};

const onFilterUpdate = patch => {
  store.setFilters(patch);
  writeQuery();
};

const onReset = () => {
  store.resetFilters();
  writeQuery();
};

// `setFilters` returns to page 1 for every change except an explicit page.
const onPageChange = page => {
  store.setFilters({ page });
  writeQuery();
};

const setViewMode = mode => {
  if (viewMode.value === mode) return;
  viewMode.value = mode;
  load();
};

const onSelect = supportCase => {
  router.push({
    name: 'support_cases_show',
    params: { displayId: String(supportCase.displayId) },
  });
};

// The drag's mutation. A category column names no stage of its own, so the
// target is resolved against the card's ticket type first; a type with no stage
// in that category has nowhere to put the card, and the board is reloaded to
// undo the drop vuedraggable has already drawn. Past that the store owns the
// optimistic move and its rollback, and this handler only owns the sentence the
// failure is reported with.
const onCardMove = async ({ supportCase, column }) => {
  const toStage = store.resolveStageForDrop(supportCase, column.stage);
  if (!toStage) {
    useAlert(t('SUPPORT_CASES.BOARD.MOVE_UNAVAILABLE'));
    loadBoard();
    return;
  }

  try {
    await store.moveCaseToStage(supportCase.id, toStage);
  } catch (error) {
    useAlert(t('SUPPORT_CASES.BOARD.MOVE_ERROR'));
  }
};

const onColumnLoadMore = column => {
  const { meta: columnMeta } = store.boardColumnFor(column.key);
  store
    .fetchBoardColumn(column.key, { page: columnMeta.currentPage + 1 })
    .catch(() => {
      // The column keeps its cards; the next "load more" is a clean retry.
    });
};

// A new ticket opens in the workroom rather than dropping into the queue: the
// person who filed it is the person about to work on it, and the list it would
// land in is sorted by activity, not by what you just did.
const onCreate = async payload => {
  try {
    const supportCase = await store.createCase(payload);
    createDialogRef.value?.close();
    useAlert(t('SUPPORT_CASES.CREATE.SUCCESS'));
    onSelect(supportCase);
  } catch (error) {
    useAlert(t('SUPPORT_CASES.CREATE.ERROR'));
  }
};

onMounted(() => {
  // The type catalogue drives the filter chip, the create dialog and the
  // board's columns, and it is workspace configuration rather than queue
  // state, so it is fetched once here rather than with every page of the list.
  store.fetchCaseTypes();
  applyQuery(route.query);
});

// The query is written by this screen and by the browser (back, forward, a
// pasted link); watching it means both paths refetch through one code path.
watch(
  () => route.query,
  query => {
    if (route.name !== 'support_cases_index') return;
    applyQuery(query);
  }
);

// A stage-grouped board's columns come from `GET /case_types`, which resolves
// after the first `load()` on a cold mount — so a queue opened on a type filter
// starts out category-grouped and re-columns itself when the types land. When
// the column set changes, any column the board has not seeded is fetched; a
// fetch already started by `load()` seeds its columns synchronously, so this
// fires only for the gap it exists to close.
watch(
  () => [isBoard.value, boardColumns.value.map(column => column.key).join(':')],
  () => {
    if (!isBoard.value) return;
    const missing = boardColumns.value.some(
      column => !store.boardColumns[column.key]
    );
    if (missing) loadBoard();
  }
);
</script>

<template>
  <div class="flex flex-col w-full h-full min-h-0 gap-4">
    <div class="flex items-center justify-between gap-4">
      <div class="flex items-baseline gap-2">
        <h1 class="mb-0 text-lg font-medium text-n-slate-12">
          {{ t('SUPPORT_CASES.HEADER.TITLE') }}
        </h1>
        <span class="text-sm tabular-nums text-n-slate-11">
          {{ t('SUPPORT_CASES.LIST.COUNT', { count: totalCount }, totalCount) }}
        </span>
      </div>

      <div class="flex items-center gap-2">
        <div
          class="flex items-center gap-0.5 p-0.5 rounded-lg bg-n-alpha-2"
          role="group"
          :aria-label="t('SUPPORT_CASES.BOARD.VIEW_TOGGLE')"
        >
          <Button
            variant="ghost"
            color="slate"
            size="xs"
            icon="i-lucide-list"
            :class="{ 'bg-n-solid-2 text-n-slate-12': !isBoard }"
            :aria-pressed="!isBoard"
            :aria-label="t('SUPPORT_CASES.BOARD.VIEW_LIST')"
            data-testid="case-view-list"
            @click="setViewMode(VIEW_MODE.LIST)"
          />
          <Button
            variant="ghost"
            color="slate"
            size="xs"
            icon="i-lucide-kanban"
            :class="{ 'bg-n-solid-2 text-n-slate-12': isBoard }"
            :aria-pressed="isBoard"
            :aria-label="t('SUPPORT_CASES.BOARD.VIEW_BOARD')"
            data-testid="case-view-board"
            @click="setViewMode(VIEW_MODE.BOARD)"
          />
        </div>

        <Button
          size="sm"
          icon="i-lucide-plus"
          :label="t('SUPPORT_CASES.CREATE.ACTION')"
          data-testid="case-create-button"
          @click="createDialogRef?.open()"
        />
      </div>
    </div>

    <CaseFilterBar
      :filters="filters"
      :case-types="caseTypeOptions"
      :counts="counts"
      :has-active-filters="isFiltered"
      @update="onFilterUpdate"
      @reset="onReset"
    />

    <div
      v-if="isBoard"
      class="flex flex-col min-h-0 grow"
      data-testid="case-board-view"
    >
      <CaseBoard
        :columns="boardColumns"
        :has-error="hasError"
        :readonly="filters.archived"
        @select="onSelect"
        @move="onCardMove"
        @load-more="onColumnLoadMore"
        @retry="load"
      />
    </div>

    <template v-else>
      <div class="flex flex-col min-h-0 overflow-y-auto grow">
        <CaseListTable
          :cases="cases"
          :is-loading="uiFlags.isFetchingList"
          :has-error="hasError"
          :is-filtered="isFiltered"
          @select="onSelect"
          @retry="load"
          @clear-filters="onReset"
        />
      </div>

      <PaginationFooter
        v-if="meta.totalPages > 1"
        :current-page="meta.currentPage"
        :total-items="meta.count"
        :items-per-page="PER_PAGE"
        class="!px-0"
        @update:current-page="onPageChange"
      />
    </template>

    <CaseCreateDialog
      ref="createDialogRef"
      :case-types="caseTypeOptions"
      :is-submitting="uiFlags.isCreatingItem"
      @submit="onCreate"
    />
  </div>
</template>
